/**
 * Pure mapping from package-run replies and local failures to the operator-visible session view.
 * Every code a view carries is one of Playjector's `macroPackageErrorCodeSchema`, realtime-api's
 * `MACRO_PACKAGE_SERVER_ERRORS`, or a host-local `MACRO_HOST_SESSION_ERRORS` entry.
 */
import {
  MACRO_PACKAGE_SERVER_ERRORS, macroPackageErrorCodeSchema,
  type MacroPackageErrorCode, type MacroPackageRunReply,
} from '@playcastdotio/Messaging';

/** Host-local failure codes. Verification carriers may forward their bounded transport subset. */
export const MACRO_HOST_SESSION_ERRORS = [
  'native_ipc_disconnected', 'native_capabilities_timeout', 'native_validation_timeout',
  'native_reply_timeout', 'native_send_failed', 'grant_request_timeout', 'input_capture_busy',
  'macro_graph_migration_required',
] as const;
export type MacroHostSessionError = (typeof MACRO_HOST_SESSION_ERRORS)[number];
export type MacroPackageServerErrorCode = (typeof MACRO_PACKAGE_SERVER_ERRORS)[number];
export type MacroSessionErrorCode = MacroPackageErrorCode | MacroPackageServerErrorCode | MacroHostSessionError;

export type MacroSessionState = 'fetching' | 'starting' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type MacroSessionView = {
  runId: string; trigger: string; variant: 'authoritative' | 'preview';
  state: MacroSessionState;
  errorCode?: MacroSessionErrorCode;
  timing?: MacroPackageRunReply['timing'];
  macroSequence?: MacroPackageRunReply['macroSequence'];
};

/** Unknown native strings collapse to the generic runtime failure rather than leaking into the UI. */
const FALLBACK_NATIVE_ERROR: MacroPackageErrorCode = 'native_runtime_error';
const knownCodes = new Set<string>([...macroPackageErrorCodeSchema.options, ...MACRO_PACKAGE_SERVER_ERRORS, ...MACRO_HOST_SESSION_ERRORS]);

export function isMacroSessionErrorCode(code: string): code is MacroSessionErrorCode {
  return knownCodes.has(code);
}

export function isTerminalSessionState(state: MacroSessionState): boolean {
  return state === 'succeeded' || state === 'failed' || state === 'cancelled';
}

export function failSessionView(view: MacroSessionView, errorCode: MacroSessionErrorCode): MacroSessionView {
  if (isTerminalSessionState(view.state)) return view;
  return { ...view, state: 'failed', errorCode };
}

export function cancelSessionView(view: MacroSessionView): MacroSessionView {
  if (isTerminalSessionState(view.state)) return view;
  const { errorCode: _ignored, ...rest } = view;
  return { ...rest, state: 'cancelled' };
}

/**
 * Applies one native reply for this session. `running` covers admission (`phase: 'loading'`), node telemetry
 * (`nodeEvent`, including `node_skipped` on unmatched branches, which is not a failure) and status snapshots.
 * Terminal views never change again.
 */
export function reduceSessionReply(view: MacroSessionView, reply: MacroPackageRunReply): MacroSessionView {
  if (isTerminalSessionState(view.state)) return view;
  const macroSequence = reply.macroSequence && reply.macroSequence.statusSequence > (view.macroSequence?.statusSequence ?? -1)
    ? reply.macroSequence : view.macroSequence;
  const progress = macroSequence ? { macroSequence } : {};
  switch (reply.outcome) {
    case 'running':
      return { ...view, ...progress, state: 'running', ...(reply.timing ? { timing: reply.timing } : {}) };
    case 'completed':
      return { ...view, ...progress, state: 'succeeded', ...(reply.timing ? { timing: reply.timing } : {}) };
    case 'cancelled':
      return { ...cancelSessionView(view), ...progress, ...(reply.timing ? { timing: reply.timing } : {}) };
    case 'failed': {
      const code = reply.errorCode && isMacroSessionErrorCode(reply.errorCode) ? reply.errorCode : FALLBACK_NATIVE_ERROR;
      return { ...failSessionView(view, code), ...progress, ...(reply.timing ? { timing: reply.timing } : {}) };
    }
    default:
      return view;
  }
}
